import * as R from "ramda";
import { toast } from "react-toastify";
import { Api } from "../../app/Api";

import { getApiUrl } from "../../app/apiPrefix";
import { AppThunk } from "../../app/store";
import { unregisterPushTokenIfEnabled } from "../settings/settingsActions";
import { nicknameSet, opponentReceived, User, userReceived } from "./userSlice";
import { isOfflineMode } from "../../app/offlineMode";

interface ApiUser extends User {
  authToken: string | null;
}

export const getUser = (): AppThunk => async (dispatch) => {
  if (isOfflineMode()) {
    // No-op in offline mode
    return;
  }
  const { data: user } = await Api.get<ApiUser>(getApiUrl("/user"));

  if (user.authToken) {
    storeAuthToken(user.authToken);
  }

  dispatch(userReceived(R.omit(["authToken"], user)));
};

const ELECTRON = Boolean(window.versions);

export const getGoogleLoginURL = (): AppThunk => async (dispatch) => {
  if (isOfflineMode()) {
    toast("Login is not available in offline mode", { type: "error" });
    return;
  }
  const { data } = await Api.post<{ url: string }>(
    getApiUrl(`/login/google`),
    null,
    {
      headers: {
        "x-context": ELECTRON ? "electron" : "web",
      },
    }
  );

  if (ELECTRON) {
    window.open(data.url);
  } else {
    window.location.href = data.url;
  }
};

export const setNickname =
  (nickname: string): AppThunk =>
  async (dispatch, getState) => {
    if (isOfflineMode()) {
      // In offline mode, just set it locally without API call
      dispatch(nicknameSet(nickname));
      return;
    }
    const id = getState().user.user?.id;
    if (!id) {
      console.error("couldn't set nickname on user without ID");
      return;
    }
    try {
      await Api.post(getApiUrl("/user/nickname"), {
        nickname,
      });
    } catch (e) {
      throw e.response?.data?.message ?? e.toString();
    }
    dispatch(nicknameSet(nickname));
  };

export const fetchOpponent =
  (id: string): AppThunk<Promise<void>> =>
  async (dispatch) => {
    if (isOfflineMode()) {
      // No-op in offline mode
      return;
    }
    const opponent = await Api.get<User>(getApiUrl("/user", id));
    dispatch(opponentReceived(opponent.data));
  };

export const storeAuthToken = (token: string) =>
  localStorage.setItem("authToken", token);
export const retrieveAuthToken = () => localStorage.getItem("authToken");
export const deleteAuthToken = () => localStorage.removeItem("authToken");

export const logout = (): AppThunk => async () => {
  if (isOfflineMode()) {
    // In offline mode, just clear local auth token
    deleteAuthToken();
    window.location.reload();
    return;
  }
  try {
    await Api.post(getApiUrl("/logout"));
    unregisterPushTokenIfEnabled();
    deleteAuthToken();
    window.location.reload();
  } catch (e) {
    toast(e.response?.data?.message ?? e.toString(), { type: "error" });
  }
};
